import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Briefcase, GraduationCap, ArrowRight } from 'lucide-react';
import Header from 'components/ui/Header';
import Footer from 'components/ui/Footer';
import BackToTop from 'components/BackToTop';
import { useSEO } from 'hooks/useSEO';
import { useScrollTracking } from 'hooks/useScrollTracking';
import { useClickTracking } from 'hooks/useClickTracking';
import { PersonSchema, BreadcrumbSchema } from 'components/JsonLd';

const About = () => {
  useSEO({
    title: 'About',
    description: 'Dr. Michelande Ridoré brings clinical expertise and applied learning sciences to healthcare organizational consulting. Learn about The Poetic Method approach to institutional transformation.',
  });

  useScrollTracking('about');
  useClickTracking('about');

  // Animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6 }
    }
  };

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15 }
    }
  };

  // Client results data - verbatim from spec
  const clientResults = [
    {
      title: 'Financial Growth',
      description: 'Millions in operational cost savings and direct reductions in high-impact clinical and operational expenses.'
    },
    {
      title: 'Operational Excellence',
      description: 'Sustained reductions in hospital-acquired conditions, double-digit improvements in clinical performance metrics, and advancements in national rankings.'
    },
    {
      title: 'Scalable, Human-Centered Systems',
      description: 'Practical frameworks and training programs that build cultures of safety, accountability, engagement, and continuous learning—with demonstrable ROI.'
    }
  ];

  // Career highlights data - verbatim from spec
  const careerHighlights = [
    {
      id: 'jackson',
      title: 'Senior Director & High Reliability Architect',
      organization: 'Jackson Health System | Miami, FL',
      description: `I designed and implemented a system-wide high-reliability framework spanning 34 departments, advancing safety culture at scale. I built and mentored a network of internal transformation coaches to sustain improvement beyond formal initiatives.

I led strategic efforts that strengthened clinical excellence, transparency, and Just Culture—positioning the system as a national voice in patient safety. I also created an institution-wide, recognition-based behavior change model that deepened team engagement, psychological safety, and frontline problem-solving.`
    },
    {
      id: 'holtz',
      title: 'Director of Quality – Pediatric Administration',
      organization: 'Holtz Children\'s & Women\'s Hospital, Jackson Health System',
      description: 'I led multi-institutional performance improvement efforts that resulted in Top-50 U.S. News & World Report specialty rankings, with significant increases in scoring.',
      metrics: [
        'Nearly 400 consecutive CLABSI-free days hospital-wide',
        '17% reduction in unexpected newborn complications',
        '44% increase in first-case on-time OR starts'
      ],
      additionalText: `I oversaw maternal health programs funded by a $14M Florida Department of Health grant and an $89K Sentara grant. These programs reduced maternal morbidity for participating populations and expanded continuity-of-care infrastructure.`
    },
    {
      id: 'vcu',
      title: 'Quality Manager, Improvement Science & Analytics',
      organization: 'Children\'s Hospital of Richmond at VCU',
      description: `I founded the hospital's first Improvement Science & Analytics Department, recruiting and training a multidisciplinary, high-performing team.

I led successful U.S. News & World Report submissions resulting in first-time specialty rankings and a 35% increase in survey scores.

Through data-informed redesign, I delivered sustained reductions in hospital-acquired conditions and strengthened clinical operations across the organization.`
    },
    {
      id: 'childrens-national',
      title: 'Program Lead – Division of Neonatology',
      organization: 'Children\'s National Health System | Washington, DC',
      description: 'I led quality initiatives that contributed to the division\'s #1 national ranking for five consecutive years.',
      metrics: [
        '45% reduction in unintended extubations',
        '49% decrease in chest X-rays per patient day',
        '$1.6M in radiology cost savings'
      ],
      additionalText: 'I increased family participation in discharge preparation by nearly 60% and improved growth outcomes for very low-birth-weight infants by 27%.'
    }
  ];

  // Research interests - verbatim from spec
  const researchInterests = [
    'Applied learning science in healthcare organizations',
    'High-reliability practices and organizational culture',
    'Poetry-informed approaches to leadership development with a focus on emotional intelligence'
  ];

  const breadcrumbItems = [
    { name: 'Home', url: 'https://thepoeticmethod.com/' },
    { name: 'About', url: 'https://thepoeticmethod.com/about' },
  ];

  return (
    <>
      <PersonSchema />
      <BreadcrumbSchema items={breadcrumbItems} />
      <div className="min-h-screen bg-cream">
        <Header />
      <main className="pt-20 sm:pt-24 md:pt-28">
        <article>
          {/* Hero Section */}
          <section
            className="bg-gradient-to-br from-navy via-navy/90 to-teal px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 lg:py-24"
            aria-labelledby="about-hero-heading"
          >
            <div className="max-w-7xl mx-auto">
              <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-8 lg:gap-10 items-center">
                {/* Left Column - Text Content */}
                <motion.div
                  className="order-2 lg:order-1 max-w-none"
                  initial={{ opacity: 0, x: -30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7 }}
                >
                  <h1
                    id="about-hero-heading"
                    className="font-display text-3xl sm:text-4xl md:text-5xl text-white mb-6 sm:mb-8"
                  >
                    About Me
                  </h1>

                  <div className="space-y-6">
                    <p className="font-body text-base sm:text-lg md:text-xl text-white/90 leading-relaxed">
                      I'm the Founder and CEO of The Poetic Method, LLC, a consulting firm devoted to helping healthcare organizations achieve measurable performance improvement without losing their humanity in the process.
                    </p>

                    <p className="font-body text-base sm:text-lg md:text-xl text-white/90 leading-relaxed">
                      For more than 15 years, I've held executive leadership roles across major academic health systems, leading large-scale transformations that generated multi-million-dollar financial returns, elevated national rankings, and significantly improved patient safety and clinical outcomes.
                    </p>
                  </div>

                  {/* Pull Quote Callout */}
                  <div className="my-8 sm:my-10 pl-6 border-l-4 border-coral">
                    <p className="font-display text-lg sm:text-xl md:text-2xl text-white italic leading-snug">
                      But numbers are not my north star.
                    </p>
                    <p className="font-display text-lg sm:text-xl md:text-2xl text-coral italic leading-snug mt-2">
                      People are.
                    </p>
                  </div>

                  <div className="space-y-6">
                    <p className="font-body text-base sm:text-lg md:text-xl text-white/90 leading-relaxed">
                      My work lives at the intersection of improvement science and expressive arts, of high-reliability systems and human tenderness, of the boardroom and the bedside. I believe sustainable excellence is not engineered through pressure alone—but cultivated through meaning, dignity, reflection, and psychological safety.
                    </p>

                    <p className="font-body text-base sm:text-lg md:text-xl text-white/90 leading-relaxed">
                      Through The Poetic Method, I partner with healthcare leaders to design transformations that honor the whole person, develop the whole leader, and strengthen the whole organization—while still delivering rigorous, measurable results.
                    </p>
                  </div>
                </motion.div>

                {/* Right Column - Image */}
                <motion.figure
                  className="order-1 lg:order-2 flex justify-center lg:justify-end lg:flex-shrink-0"
                  initial={{ opacity: 0, x: 30 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.7, delay: 0.2 }}
                >
                  <div className="relative w-full max-w-[220px] sm:max-w-[260px] lg:w-[280px] mx-auto lg:mx-0">
                    <div className="absolute inset-0 bg-coral/20 rounded-2xl transform rotate-3" aria-hidden="true"></div>
                    <img
                      src="/assets/images/cover.jpg"
                      alt="Dr. Michelande Ridoré, founder and CEO of The Poetic Method, professional portrait"
                      className="relative rounded-2xl shadow-2xl w-full h-auto object-cover aspect-[2/3]"
                      loading="eager"
                      width="600"
                      height="900"
                      decoding="async"
                    />
                  </div>
                </motion.figure>
              </div>
            </div>
          </section>

          {/* Client Results Section */}
          <section
            className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 bg-white"
            aria-labelledby="results-heading"
          >
            <div className="max-w-7xl mx-auto">
              <motion.h2
                id="results-heading"
                className="font-display text-2xl sm:text-3xl md:text-4xl text-navy mb-8 sm:mb-10 md:mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                My clients experience:
              </motion.h2>

              <motion.div
                className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8"
                variants={staggerContainer}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
              >
                {clientResults.map((result, index) => (
                  <motion.div
                    key={index}
                    className="bg-cream rounded-xl p-6 sm:p-8 border border-mist hover:shadow-lg hover:-translate-y-1 transition-all duration-300"
                    variants={fadeInUp}
                  >
                    <h3 className="font-display text-lg sm:text-xl text-navy mb-3 sm:mb-4">
                      {result.title}
                    </h3>
                    <p className="font-body text-sm sm:text-base text-charcoal leading-relaxed">
                      {result.description}
                    </p>
                  </motion.div>
                ))}
              </motion.div>
            </div>
          </section>

          {/* Career Highlights Section */}
          <section
            className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 bg-cream"
            aria-labelledby="career-heading"
          >
            <div className="max-w-6xl mx-auto">
              <motion.div
                className="flex items-center gap-3 mb-8 sm:mb-10 md:mb-12"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <Briefcase className="w-6 h-6 sm:w-7 sm:h-7 text-teal" aria-hidden="true" />
                <h2 id="career-heading" className="font-display text-2xl sm:text-3xl md:text-4xl text-navy">
                  Career Highlights
                </h2>
              </motion.div>

              <div className="space-y-8">
                {careerHighlights.map((role, index) => (
                  <motion.article
                    key={role.id}
                    className="bg-white rounded-xl p-6 sm:p-7 border border-mist shadow-sm"
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <h3 className="font-display text-xl sm:text-2xl text-navy mb-2">
                      {role.title}
                    </h3>
                    <p className="font-body text-sm sm:text-base text-teal font-medium mb-5">
                      {role.organization}
                    </p>

                    <div className="font-body text-sm sm:text-base text-charcoal leading-relaxed whitespace-pre-line space-y-3">
                      {role.description}
                    </div>

                    {role.metrics && (
                      <div className="mt-6 sm:mt-8 grid grid-cols-1 sm:grid-cols-3 gap-4">
                        {role.metrics.map((metric, idx) => (
                          <div
                            key={idx}
                            className="bg-teal/5 rounded-lg p-4 border-l-4 border-teal"
                          >
                            <p className="font-body text-sm sm:text-base text-navy font-medium">
                              {metric}
                            </p>
                          </div>
                        ))}
                      </div>
                    )}

                    {role.additionalText && (
                      <div className="mt-6 font-body text-sm sm:text-base text-charcoal leading-relaxed whitespace-pre-line space-y-3">
                        {role.additionalText}
                      </div>
                    )}
                  </motion.article>
                ))}
              </div>
            </div>
          </section>

          {/* Education Section */}
          <section
            className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 bg-white"
            aria-labelledby="education-heading"
          >
            <div className="max-w-6xl mx-auto">
              <motion.div
                className="flex items-center gap-3 mb-8 sm:mb-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <GraduationCap className="w-6 h-6 sm:w-7 sm:h-7 text-teal" aria-hidden="true" />
                <h2 id="education-heading" className="font-display text-2xl sm:text-3xl md:text-4xl text-navy">
                  Education
                </h2>
              </motion.div>

              <motion.div
                className="max-w-3xl"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                <div className="space-y-6">
                  <div>
                    <h3 className="font-display text-xl sm:text-2xl text-navy mb-2">
                      Doctor of Education in Applied Learning Sciences
                    </h3>
                    <p className="font-body text-base sm:text-lg text-teal font-medium mb-4">
                      University of Miami
                    </p>
                    <p className="font-body text-sm sm:text-base text-charcoal leading-relaxed">
                      Dissertation focused on self-directed learning-based digital personal learning environments to build patient health literacy in communities with social vulnerabilities and health disparities.
                    </p>
                  </div>

                  <div className="pt-6 border-t border-mist">
                    <h3 className="font-display text-lg sm:text-xl text-navy mb-4">
                      Ongoing Research Interests
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {researchInterests.map((interest, index) => (
                        <span
                          key={index}
                          className="bg-teal/10 text-teal px-3 sm:px-4 py-1.5 sm:py-2 rounded-full font-body text-xs sm:text-sm"
                        >
                          {interest}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>


              </motion.div>
            </div>
          </section>

          {/* Philosophy Section */}
          <section
            className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-10 sm:py-12 md:py-16 bg-cream"
            aria-labelledby="philosophy-heading"
          >
            <div className="max-w-5xl mx-auto text-center">
              <motion.h2
                id="philosophy-heading"
                className="font-display text-2xl sm:text-3xl md:text-4xl text-navy mb-4 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                A Different Kind of Partner
              </motion.h2>

              <motion.p
                className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed mb-6 sm:mb-8"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                My work is for organizations ready to move beyond surface-level optimization—to do the deeper work of becoming safer, more compassionate, more resilient, and more honest.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <p className="font-display text-base sm:text-lg text-navy mb-4 sm:mb-6">I believe:</p>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 sm:gap-4 md:gap-5">
                  <div className="bg-teal/5 rounded-lg p-4 sm:p-5 border border-teal/20">
                    <p className="font-body text-sm sm:text-base text-teal leading-relaxed">
                      Systems change when people feel seen.
                    </p>
                  </div>

                  <div className="bg-teal/5 rounded-lg p-4 sm:p-5 border border-teal/20">
                    <p className="font-body text-sm sm:text-base text-teal leading-relaxed">
                      Metrics improve when meaning is restored.
                    </p>
                  </div>

                  <div className="bg-teal/5 rounded-lg p-4 sm:p-5 border border-teal/20">
                    <p className="font-body text-sm sm:text-base text-teal leading-relaxed">
                      And excellence is sustained when leaders learn to listen—to data and to the human heart.
                    </p>
                  </div>
                </div>
              </motion.div>
            </div>
          </section>

          {/* Scripture Section */}
          <section
            className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 bg-gradient-to-br from-navy/95 via-navy/90 to-teal/85"
            aria-label="Scripture reflection"
          >
            <motion.figure
              className="max-w-5xl mx-auto text-center"
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <blockquote className="font-display text-lg sm:text-xl md:text-2xl text-white/90 leading-relaxed italic">
                <p className="mb-4">
                  He made the moon to mark the seasons,<br />
                  and the sun knows when to go down.<br />
                  You bring darkness, it becomes night,<br />
                  and all the beasts of the forest prowl.<br />
                  The lions roar for their prey<br />
                  and seek their food from God.<br />
                  The sun rises, and they steal away;<br />
                  they return and lie down in their dens.<br />
                  Then people go out to their work,<br />
                  to their labor until evening.
                </p>
                <p>
                  How many are your works, Lord!<br />
                  In wisdom you made them all;<br />
                  the earth is full of your creatures.
                </p>
              </blockquote>
              <figcaption className="mt-6 sm:mt-8 font-body text-sm sm:text-base text-white/80">
                —Psalms 104:19-24
              </figcaption>
            </motion.figure>
          </section>

          {/* CTA Section */}
          <section
            className="px-4 sm:px-6 md:px-12 lg:px-16 xl:px-24 py-12 sm:py-16 md:py-20 bg-cream"
            aria-labelledby="cta-heading"
          >
            <div className="max-w-4xl mx-auto text-center">
              <motion.h2
                id="cta-heading"
                className="font-display text-2xl sm:text-3xl md:text-4xl text-navy mb-4 sm:mb-6"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
              >
                Let's Connect
              </motion.h2>

              <motion.p
                className="font-body text-base sm:text-lg md:text-xl text-charcoal leading-relaxed mb-8 sm:mb-10"
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
              >
                If you're seeking a partner who can deliver measurable improvement and meaningful transformation, I'd love to begin a conversation.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
              >
                <Link
                  to="/contact"
                  className="inline-flex items-center gap-2 bg-coral text-white px-8 sm:px-10 py-3 sm:py-4 rounded-lg font-body font-semibold text-base sm:text-lg transition-all duration-300 hover:bg-coral/90 hover:shadow-xl hover:scale-105 focus:outline-none focus:ring-2 focus:ring-coral focus:ring-offset-2"
                >
                  Contact
                  <ArrowRight size={20} aria-hidden="true" />
                </Link>
              </motion.div>
            </div>
          </section>
        </article>
      </main>
        <Footer />
        <BackToTop />
      </div>
    </>
  );
};

export default About;
